import {
  XIcon,
  ArrowLeftIcon,
  CameraIcon,
  ListStarIcon,
  WarningIcon,
  BellIcon,
} from "@phosphor-icons/react";
import { useI18N } from "common/hooks";
import { cn } from "@/lib/utils";

export type ContributionStep = "intro" | "options" | "unsupported";
export type ContributionVariant = "card" | "sheet" | "page";

export const CONTRIBUTION_OPTIONS = [
  { id: "photos", labelKey: "poiAddPhotos", Icon: CameraIcon },
  { id: "experiences", labelKey: "poiShareExperiences", Icon: ListStarIcon },
  { id: "alerts", labelKey: "poiFlagAlerts", Icon: WarningIcon },
  { id: "subscribe", labelKey: "poiSubscribeUpdates", Icon: BellIcon },
] as const;

type Props = {
  step: ContributionStep;
  selected: string[];
  creating: boolean;
  /** card = desktop anchored popover, sheet = mobile bottom sheet, page = mobile full-screen */
  variant?: ContributionVariant;
  onShowOptions: () => void;
  onToggleOption: (id: string) => void;
  onContinue: () => void;
  onClose: () => void;
  onBack?: () => void;
};

const primaryBtn =
  "w-full bg-bg-brand-primary-default text-white rounded-lg py-3 label-l-bold hover:bg-bg-brand-primary-hover focus-blue";

type Option = (typeof CONTRIBUTION_OPTIONS)[number];

/**
 * Opt-in contribution flow shown when a native Google POI is clicked in
 * "All places" mode and the venue isn't yet in our DB.
 *   intro -> options (multi-select) -> create.  "unsupported" is terminal.
 * Renders as a desktop popover, a mobile bottom sheet, or a full-screen page.
 */
export function PoiContributionCard({
  step,
  selected,
  creating,
  variant = "card",
  onShowOptions,
  onToggleOption,
  onContinue,
  onClose,
  onBack,
}: Props) {
  const i18n = useI18N();

  const OptionTile = ({
    option: { id, labelKey, Icon },
    large,
  }: {
    option: Option;
    large: boolean;
  }) => {
    const isOn = selected.includes(id);
    return (
      <button
        type="button"
        aria-pressed={isOn}
        onClick={(e) => {
          e.stopPropagation();
          onToggleOption(id);
        }}
        className={cn(
          "flex flex-col items-start gap-2 rounded-xl border text-left transition-colors focus-blue",
          large ? "p-4 min-h-[120px]" : "p-3",
          isOn
            ? "border-border-token-brand-strong bg-bg-brand-ghost-hover"
            : "border-border-token-neutral-minimal hover:bg-bg-brand-ghost-hover",
        )}
      >
        <span
          className={cn(
            "flex items-center justify-center rounded-md bg-bg-brand-ghost-hover",
            large ? "w-10 h-10" : "w-8 h-8",
          )}
        >
          <Icon size={large ? 22 : 18} className="text-fg-brand-subtle" />
        </span>
        <span className={large ? "title-s-semibold" : "label-m-medium"}>
          {i18n.get(labelKey)}
        </span>
      </button>
    );
  };

  const continueBtn = (
    <button
      type="button"
      disabled={selected.length === 0 || creating}
      onClick={(e) => {
        e.stopPropagation();
        onContinue();
      }}
      className={cn(
        primaryBtn,
        (selected.length === 0 || creating) && "opacity-40 cursor-not-allowed",
      )}
    >
      {creating ? i18n.get("loading") : i18n.get("poiContinue")}
    </button>
  );

  // Mobile full-screen "What you can do" page.
  if (variant === "page") {
    return (
      <div className="flex flex-col h-full w-full bg-bg-brand-secondary-default text-fg-neutral-strong">
        <div className="flex items-center px-4 pt-4 pb-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              (onBack ?? onClose)();
            }}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-bg-brand-ghost-hover focus-blue"
            aria-label={i18n.get("back")}
          >
            <ArrowLeftIcon size={22} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4">
          <h1 className="title-l-bold mb-1">{i18n.get("poiWhatYouCanDo")}</h1>
          <p className="body-m-medium text-fg-neutral-medium mb-5">
            {i18n.get("poiSelectApply")}
          </p>
          <div className="grid grid-cols-2 gap-3">
            {CONTRIBUTION_OPTIONS.map((o) => (
              <OptionTile key={o.id} option={o} large />
            ))}
          </div>
        </div>
        <div className="p-4 border-t border-border-token-neutral-minimal">
          {continueBtn}
        </div>
      </div>
    );
  }

  const isSheet = variant === "sheet";

  return (
    <div
      className={cn(
        "relative bg-bg-brand-secondary-default text-fg-neutral-strong",
        isSheet
          ? "rounded-2xl p-5 w-full shadow-xl"
          : step === "options"
            ? // Desktop "What you can do" modal — larger with symmetric padding.
              "rounded-2xl p-6 w-[480px] max-w-[92vw] shadow-xl"
            : "rounded-xl p-4 w-[320px] max-w-[80vw]",
      )}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full hover:bg-bg-brand-ghost-hover focus-blue"
        aria-label={i18n.get("close")}
        type="button"
      >
        <XIcon size={16} weight="bold" />
      </button>

      {step === "intro" && (
        <div className="pr-6">
          <h3 className="body-m-medium text-fg-neutral-medium mb-1">
            {i18n.get("poiNotHereTitle")}
          </h3>
          <p className="title-s-semibold text-fg-neutral-strong mb-4">
            {i18n.get("poiNotHereSubtitle")}
          </p>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onShowOptions();
            }}
            className={primaryBtn}
          >
            {i18n.get("poiShowMeHow")}
          </button>
        </div>
      )}

      {step === "options" && (
        <div>
          <h3 className="title-l-bold mb-1 pr-8">
            {i18n.get("poiWhatYouCanDo")}
          </h3>
          <p className="body-m-medium text-fg-neutral-medium mb-5">
            {i18n.get("poiSelectApply")}
          </p>
          <div className="grid grid-cols-2 gap-3 mb-5">
            {CONTRIBUTION_OPTIONS.map((o) => (
              <OptionTile key={o.id} option={o} large />
            ))}
          </div>
          {continueBtn}
        </div>
      )}

      {step === "unsupported" && (
        <div className="pr-6">
          <h3 className="title-s-semibold text-fg-neutral-strong mb-1">
            {i18n.get("poiUnsupportedCardTitle")}
          </h3>
          <p className="body-m-medium text-fg-neutral-medium mb-4">
            {i18n.get("poiUnsupportedSubtitle")}
          </p>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className={primaryBtn}
          >
            {i18n.get("poiGotIt")}
          </button>
        </div>
      )}
    </div>
  );
}
